import { get, isNil } from 'lodash';

import { __t, applyTranslations } from './language';
import { logError } from './log';
import { isOfferUpdateSystemMessage } from './messages';

import type {
  Campaign,
  DeliverableTileTab,
  DeliverablesFilterCampaign,
  MessengerConversation,
  MessengerMessage,
  MessengerUnreadChatsMap,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

applyTranslations(require('./i18n/messenger-util.i18n.json'));

export const UNREAD_CHATS_POLLING_INTERVAL = 30 * 1000;
export const POLLING_INTERVAL = 5000;

export const FILTER_OPTIONS = [
  'all',
  'accepted',
  'pending',
  'revoked'
];

// https://github.bus.zalan.do/Team-Collab/collab-backend/blob/dev/src/main/java/com/collabary/common/services/messenger/MessengerService.java#L61
export const SUPPORTED_MIMETYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-rar-compressed',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'application/mp4',
  'video/3gpp',
  'video/3gpp2',
  'video/x-msvideo',
  'video/mp4',
  'video/quicktime',
  'video/ogg',
  'video/webm',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/mpeg',
  'video/avi',
  'text/plain',
  'text/csv',
  'application/pdf'
];

export function isSystemMessage(message: MessengerMessage): boolean {
  return message && message.type === 'system';
}

export function getSystemMessageText(
  message: MessengerMessage,
  campaign: Campaign,
  influencerProfile: SubcampaignInfluencerProfile | undefined | null,
  subcampaign: Subcampaign | undefined | null,
  isBrand: boolean
) {
  try {
    if (!message || !message.metadata.type ||
      !campaign || !campaign.id ||
      !influencerProfile || !influencerProfile.id
    ) {
      return '';
    }

    const messageType = isBrand ? 'brand' : 'influencer';

    if (
      message.metadata.type === 'INFLUENCER_POST'
      && (message.metadata.postType === 'tiktok_video' || message.metadata.postType === 'youtube_video')
    ) {
      return __t(`messenger_util.system_message.${messageType}.influencer_video`, {
        influencer: influencerProfile.name
      });
    }

    if (message.metadata.type === 'SET_SHIPPING_ADDRESS' && isNil(message.metadata.subcampaign.shippingAddress.address1)) {
      return __t(`messenger_util.system_message.${messageType}.set_phone`);
    }

    if (message.metadata.type === 'OFFER_PRODUCT_SHIPMENT_ADDED') {
      return __t(`messenger_util.system_message.${messageType}.offer_product_shipment_added`, {
        brand: campaign?.brandProfile?.name,
        campaignLink: `/campaigns/${campaign?.id}/details/${subcampaign?.id}/`
      });
    }

    const hasNewOffer = subcampaign && subcampaign.updatedOffer;

    if (message.metadata.type === 'NEGOTIATION_REOPEN' && !hasNewOffer) {
      return __t(`messenger_util.system_message.${messageType}.offer_updated_while_being_pending`, {
        brand: campaign.brandProfile && campaign.brandProfile.name
      });
    }

    if (isOfferUpdateSystemMessage(message)) {
      return __t(`messenger_util.system_message.${messageType}.negotiation_reopen`, {
        campaignName: campaign.name
      });
    }

    if (message.metadata.type === 'OFFER_QUOTE') {
      return __t(`messenger_util.system_message.${messageType}.offer_quote`, {
        offer: subcampaign?.offer?.quote
      });
    }

    if (message.metadata.type === 'VARIABLE_PAYMENTS_UPDATED' && !message.metadata.variablePaymentsSum) {
      return __t(`messenger_util.system_message.${messageType}.variable_payments_deleted`, {
        brand: campaign.brandProfile && campaign.brandProfile.name
      });
    }

    if (
      message.metadata.type === 'CONTENT_FOR_APPROVAL_NOTE_ADDED_V2' ||
      message.metadata.type === 'CONTENT_FOR_APPROVAL_NOTE_EDITED_V2' ||
      message.metadata.type === 'CONTENT_FOR_APPROVAL_NOTE_DELETED_V2'
    ) {
      const brandName = message.metadata?.authorType === 'brand' && campaign.brandProfile && campaign.brandProfile.name;
      const influencerName = message.metadata?.authorType === 'influencer' && influencerProfile.name;

      return __t(`messenger_util.system_message.${messageType}.${message.metadata.type.toLowerCase()}`, {
        name: brandName || influencerName
      });
    }

    return __t(`messenger_util.system_message.${messageType}.${message.metadata.type.toLowerCase()}`, {
      brand: campaign.brandProfile && campaign.brandProfile.name,
      campaignName: campaign.name,
      influencer: influencerProfile.name,
      deliverableName: get(message, 'metadata.payload.deliverable.name')
    });
  } catch (e) {
    logError(e);
    return '';
  }
}

export function getLastMessage(
  conversation: MessengerConversation,
  unreadChatsMap: MessengerUnreadChatsMap
): MessengerMessage {
  const unreadMessages = unreadChatsMap[conversation.externalId];

  // No unread messages for this conversation, so we take the last conversation message
  if (!unreadMessages) {
    return conversation.lastMessage;
  }

  // There are unread messages. We have to compare the last conversation message with the most
  // recent unread message (the most recent unread message is not always the most recent message).
  // For example, the following order is possible:
  // - Read message
  // - Unread message
  // - Bulk message (does not show up in unread messages)
  const conversationMessageCreatedAt = get(conversation, 'lastMessage.createdAt') || '';
  const mostRecentUnreadMessageCreatedAt = get(unreadMessages, 'lastMessage.createdAt') || '';

  return (conversationMessageCreatedAt && conversationMessageCreatedAt < mostRecentUnreadMessageCreatedAt)
    ? unreadMessages.lastMessage
    : conversation.lastMessage;
}

export const getSystemMessageContentLinkForBrand = (
  campaignId: number,
  subcampaignId: number,
  deliverableId: number | undefined | null,
  filter: DeliverablesFilterCampaign,
  tab: DeliverableTileTab = null,
  isCcm: boolean = false
): string => {
  if (!isNil(tab)) {
    return `/campaigns${isCcm ? '/ccm' : ''}/${campaignId}/deliverables?subcampaignId=${subcampaignId}&tab=${tab}${deliverableId ? `#deliverable-${deliverableId}` : ''}`;
  }

  return `/campaigns${isCcm ? '/ccm' : ''}/${campaignId}/deliverables?subcampaignId=${subcampaignId}&filter=${filter}${deliverableId ? `#deliverable-${deliverableId}` : ''}`;
};

export const getSystemMessageContentLinkForInfluencer = (
  campaignId: number,
  subcampaignId: number,
  deliverableId?: number | null,
  tab: DeliverableTileTab = null,
  isCcm: boolean = false
): string => {
  if (!isNil(tab)) {
    return `/campaigns${isCcm ? '/ccm' : ''}/${campaignId}/details/${subcampaignId}?tab=${tab}${deliverableId ? `#deliverable-${deliverableId}` : ''}`;
  }

  return `/campaigns${isCcm ? '/ccm' : ''}/${campaignId}/details/${subcampaignId}${deliverableId ? `#deliverable-${deliverableId}` : ''}`;
};
